import { itemsApi } from '@/api';
import { saveBlobResponse } from '@/api/utils/download';
import {
  Item,
  ItemFacetsFilters,
  ItemFacetsResponse,
  ItemWithAllPrices,
  ManufacturerCandidate,
} from '@/api/items/items.types.ts';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const useGetAllItems = (
  priceListId?: number,
  options?: UseQueryOptions<Item[], AxiosError>,
) => {
  return useQuery<Item[], AxiosError>({
    queryKey: ['items', priceListId],
    queryFn: () => itemsApi.getAllItems(priceListId).then((res) => res.data),
    ...options,
  });
};

export const useGetAllItemsWithAllPrices = (
  options?: UseQueryOptions<ItemWithAllPrices[], AxiosError>,
) => {
  return useQuery<ItemWithAllPrices[], AxiosError>({
    queryKey: ['itemsWithAllPrices'],
    queryFn: () => itemsApi.getAllItemsWithAllPrices().then((res) => res.data),
    ...options,
  });
};

export const useGetItemById = (
  itemId: string,
  priceListId?: number,
  options?: UseQueryOptions<Item, AxiosError>,
) => {
  return useQuery<Item, AxiosError>({
    queryKey: ['item', itemId, priceListId],
    queryFn: () =>
      itemsApi.getItemById(itemId, priceListId).then((res) => res.data),
    ...options,
  });
};

export const useSyncItems = (
  options?: UseMutationOptions<void, AxiosError, void>,
) => {
  return useMutation<void, AxiosError, void>({
    mutationFn: () => itemsApi.syncItems().then(() => undefined),
    ...options,
  });
};

export const useItemFacets = (
  filters?: ItemFacetsFilters,
  options?: UseQueryOptions<ItemFacetsResponse, AxiosError>,
) => {
  return useQuery<ItemFacetsResponse, AxiosError>({
    queryKey: ['itemFacets', filters],
    queryFn: () => itemsApi.getItemFacets(filters).then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * All synced manufacturers as `{ id, name }[]`. Used by the shared
 * `ManufacturerPicker` (and anywhere else that needs to render a filterable
 * manufacturer list). The set is small (≤ a few hundred rows) so we fetch
 * once and filter client-side.
 */
export const useManufacturerCandidates = (
  options?: UseQueryOptions<ManufacturerCandidate[], AxiosError>,
) =>
  useQuery<ManufacturerCandidate[], AxiosError>({
    queryKey: ['manufacturers', 'candidates'],
    queryFn: () => itemsApi.getManufacturers().then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

interface ExportItemsVars {
  itemIds: string[];
  format: 'xlsx' | 'csv';
}

/**
 * Exports the selected internal article rows (MEK + all price lists + stock)
 * to xlsx/csv and triggers the file download via the shared blob helper.
 */
export const useExportItemsWithAllPrices = (
  options?: UseMutationOptions<void, AxiosError, ExportItemsVars>,
) =>
  useMutation<void, AxiosError, ExportItemsVars>({
    mutationFn: async ({ itemIds, format }) => {
      const response = await itemsApi.exportItemsWithAllPrices(itemIds, format);
      saveBlobResponse(response, `VERTRAULICH_elink_artikelexport.${format}`);
    },
    ...options,
  });
